import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * A named audience inside one tenant ("QA Review"), referenced by id from form
 * configuration: a signature field's allowed signers, a form's allowed
 * completers (z8nrz7dt45).
 */
export interface UserGroup {
  id: string
  name: string
  description: string | null
  members: { id: string; name: string; email: string; enabled: boolean }[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

// Reads need users.manage or forms.create/edit (the builder's picker lists
// groups); every write needs users.manage in the selected tenant.
export const userGroupsApi = {
  list: () => request<UserGroup[]>('/user-groups'),

  create: (body: { name: string; description?: string; memberUserIds?: string[] }) =>
    request<UserGroup>('/user-groups', { method: 'POST', body: JSON.stringify(body) }),

  update: (id: string, body: { name?: string; description?: string | null }) =>
    request<UserGroup>(`/user-groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  setMembers: (id: string, userIds: string[]) =>
    request<UserGroup>(`/user-groups/${id}/members`, {
      method: 'PUT',
      body: JSON.stringify({ userIds }),
    }),

  // 409 while any form version references the group.
  remove: (id: string) => request<{ ok: true }>(`/user-groups/${id}`, { method: 'DELETE' }),
}
